import { Injectable } from '@angular/core';

export interface SubTabConfig {
    [parentTab: string]: string[];
}

export interface ApplicationConfig {
    id: number;
    name: string;
    availableTabs: string[];
    subTabs: SubTabConfig;
}

@Injectable({
    providedIn: 'root'
})
export class ApplicationConfigService {

    private readonly ALL_TABS = [
        'Assets', 'Devices', 'Incidents', 'Users',
        'Asset Sensor Mapping', 'Location Management',
        'Reports', 'Master'
    ] as const;

    // Default sub-tabs for each parent tab
    private readonly DEFAULT_SUB_TABS: SubTabConfig = {
        'Devices': ['Gateways', 'Sensors', 'Hooters'],
        'Master': ['Roles', 'Environment', 'Excursion', 'Sensor Type', 'Capability', 'Checklist']
    };

    private readonly APPLICATION_CONFIGS: ApplicationConfig[] = [
        {
            id: 1,
            name: 'Storage',
            availableTabs: ['Assets', 'Devices', 'Users', 'Asset Sensor Mapping', 'Location Management', 'Reports', 'Master'],
            subTabs: {
                'Devices': ['Gateways', 'Sensors', 'Hooters'],
                'Master': ['Roles', 'Environment', 'Excursion', 'Sensor Type', 'Capability']
            }
        },
        {
            id: 2,
            name: 'Transport',
            availableTabs: ['Devices', 'Users', 'Location Management', 'Master'],
            subTabs: {
                'Devices': ['Sensors'],
                'Master': ['Roles', 'Environment', 'Excursion', 'Capability', 'Checklist']
            }
        }
    ];

    // Public API Methods

    /**
     * Get all application configurations
     */
    getApplicationConfigs(): ApplicationConfig[] {
        return [...this.APPLICATION_CONFIGS];
    }

    /**
     * Get configuration for a specific application
     */
    getApplicationConfig(applicationId: number): ApplicationConfig | null {
        return this.APPLICATION_CONFIGS.find(config => config.id === applicationId) || null;
    }

    /**
     * Get available tabs for a specific application
     */
    getAvailableTabsForApplication(applicationId: number): string[] {
        const config = this.getApplicationConfig(applicationId);
        return config ? [...config.availableTabs] : [...this.ALL_TABS];
    }

    /**
     * Get available sub-tabs for a specific parent tab and application
     */
    getAvailableSubTabsFor(applicationId: number, parentTab: string): string[] {
        const config = this.getApplicationConfig(applicationId);
        const subTabs = config?.subTabs[parentTab];
        return subTabs ? [...subTabs] : [...(this.DEFAULT_SUB_TABS[parentTab] || [])];
    }

    /**
     * Get all sub-tabs configuration for an application
     */
    getAllSubTabsForApplication(applicationId: number): SubTabConfig {
        const config = this.getApplicationConfig(applicationId);
        return config ? { ...config.subTabs } : { ...this.DEFAULT_SUB_TABS };
    }

    /**
     * Get default sub-tabs for a parent tab
     */
    getDefaultSubTabsFor(parentTab: string): string[] {
        return [...(this.DEFAULT_SUB_TABS[parentTab] || [])];
    }

    /**
     * Get all possible tabs
     */
    getAllTabs(): readonly string[] {
        return this.ALL_TABS;
    }

    /**
     * Get all parent tabs that have sub-tabs
     */
    getParentTabsWithSubTabs(): string[] {
        return Object.keys(this.DEFAULT_SUB_TABS);
    }

    /**
     * Check if a tab is available for a specific application
     */
    isTabAvailable(applicationId: number, tabName: string): boolean {
        return this.getAvailableTabsForApplication(applicationId).includes(tabName);
    }

    /**
     * Check if a sub-tab is available for a specific parent tab and application
     */
    isSubTabAvailable(applicationId: number, parentTab: string, subTabName: string): boolean {
        return this.getAvailableSubTabsFor(applicationId, parentTab).includes(subTabName);
    }

    /**
     * Check if a parent tab has sub-tabs
     */
    hasSubTabs(parentTab: string): boolean {
        return parentTab in this.DEFAULT_SUB_TABS;
    }

    getApplicationName(applicationId: number): string {
        const config = this.getApplicationConfig(applicationId);
        return config?.name || 'Unknown Application';
    }
}